import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import PropTypes from 'prop-types';
import jwt from 'jsonwebtoken';
import useCountdown from '../../hooks/useCountdown';
import EmailVerificationModal from '../../components/auth/EmailVerificationModal';
import EnrollmentInstantiationType from '../../enums/EnrollmentInstantiationType';
import EnrollmentInstantiationApi from '../EnrollmentFormContextProvider/EnrollmentInstantiationApi';
import { EnrollmentContext } from '../EnrollmentContextProvider';
import enrollmentAction from '../EnrollmentContextProvider/enrollmentAction';
import { EnrollmentFormContext } from '../EnrollmentFormContextProvider';
import LegalEnrollmentApi from '../../domain/EnrollmentLegal/api-enrollmentLegal';

// COMENTARIO

export const EnrollmentInstantiationContext = createContext({
  instantiateEnrollment: () => {},
  isLoading: false,
});

const EnrollmentInstantiationProvider = ({ children }) => {
  const [verificationToken, setVerificationToken] = useState();
  const [email, setEmail] = useState();
  const [instantiationArgs, setInstantiationArgs] = useState();
  const [onValidate, setOnValidate] = useState(() => {});
  const [countdownTime, setCountdownTime] = useState(300);

  const [hasExpired, setExpired] = useState(false);
  const [isResendEnabled, setResendEnabled] = useState(true);
  const [isResending, setResending] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const enrollmentContext = useContext(EnrollmentContext);
  const { enrollmentForm } = useContext(EnrollmentFormContext);

  const handleExpiration = useCallback(() => {
    setExpired(true);
    setResendEnabled(true);
  }, [setExpired]);

  const [verificationCountdown, restartCountdown] = useCountdown(100000, handleExpiration);

  useEffect(() => {
    if (isModalVisible) {
      restartCountdown(countdownTime);
    }
  }, [isModalVisible, restartCountdown]);

  const handleCancel = () => {
    setModalVisible(false);
  };

  const instantiateEnrollment = useCallback(
    (type, payload, onValidateCallback, timeInSeconds) => {
      setOnValidate(() => onValidateCallback);
      setLoading(true);

      const legalRepresentatives = enrollmentForm.getFieldValue('legalRepresentatives');
      const mainLegalRepresentative = legalRepresentatives.find(
        ({ isTheMainRepresentative }) => isTheMainRepresentative
      );

      setEmail(mainLegalRepresentative.email);

      let doRequest;
      switch (type) {
        case EnrollmentInstantiationType.NewLegalPersonRut:
          doRequest = EnrollmentInstantiationApi.instantiateExistingRutLegalPersonEnrollment({
            ...payload,
            isMadeByThirdParty: payload.isMadeByThirdParty ? payload.isMadeByThirdParty : false,
          });
          break;
        default:
          break;
      }

      doRequest
        .then((response) => {
          const { token } = response.data;
          if (timeInSeconds) {
            setCountdownTime(timeInSeconds);
          }
          setExpired(false);
          setModalVisible(true);
          setVerificationToken(token);
          restartCountdown(timeInSeconds || countdownTime);
          setInstantiationArgs([type, payload, onValidateCallback, timeInSeconds]);
          if (isResendEnabled) {
            setResendEnabled(false);
          }
        })
        .catch((error) => {
          message.error(error.message || 'Ocurrió un error. Intente nuevamente.');
        })
        .finally(() => {
          setLoading(false);
          setResending(false);
        });
    },
    [restartCountdown, setModalVisible, setModalVisible]
  );

  const resend = useCallback(() => {
    setResending(true);

    instantiateEnrollment(...instantiationArgs);
  }, [setResending, setLoading, instantiateEnrollment, instantiationArgs]);

  const handleSubmit = useCallback(
    (code) => {
      setLoading(true);

      EnrollmentInstantiationApi.validateMail(verificationToken, code)
        .then((response) => {
          const { token } = response.data;

          const { id } = jwt.decode(token);

          enrollmentContext.dispatch({
            type: enrollmentAction.SET_AUTH_TOKEN,
            payload: token,
          });

          LegalEnrollmentApi.getLegalEnrollment(id).then((enrollmentResponse) => {
            enrollmentContext.dispatch({
              type: enrollmentAction.SET_ENROLLMENT,
              payload: enrollmentResponse.data,
            });

            enrollmentForm.setFieldsValue({
              legalRepresentatives: enrollmentResponse.data.legalRepresentatives,
            });

            setModalVisible(false);

            onValidate();
          });
        })
        .catch((error) => {
          message.error(error.message || 'Ocurrió un error con validateMail. Intente nuevamente.');
          setLoading(false);
        })
        .finally(() => {
          // setLoading(false);
        });
    },
    [enrollmentContext.dispatch, onValidate, setModalVisible, verificationToken]
  );

  const contextValue = useMemo(
    () => ({
      instantiateEnrollment,
      isLoading,
    }),
    [instantiateEnrollment, isLoading]
  );

  return (
    <EnrollmentInstantiationContext.Provider value={contextValue}>
      <EmailVerificationModal
        email={email}
        isLoading={isLoading}
        isVisible={isModalVisible}
        isResending={isResending}
        hasExpired={hasExpired}
        timeLeft={verificationCountdown}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        resend={resend}
        isResendEnabled={isResendEnabled}
      />
      {children}
    </EnrollmentInstantiationContext.Provider>
  );
};

EnrollmentInstantiationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default EnrollmentInstantiationProvider;
